import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { downloadErrorReport, getErrorLogs, startMigration } from "../../api"; // Adjust import based on your API structure
import MigrationProgress from "./MigrationProgress";
import MigrationReport from "./MigrationReport";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import StartMigration from "./StartMigration";
import { useToast } from "@shared/hooks/use-toast";

const MigrationExecution = () => {
  const { sessionId } = useParams();
  const [currentStep, setCurrentStep] = useState("ready"); // 'ready', 'progress', 'report'
  const [taskId, setTaskId] = useState(null);
  const [migrationData, setMigrationData] = useState(null);
  const [migrationLogs, setMigrationLogs] = useState("");
  const [errorLogs, setErrorLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMigrationStarted, setIsMigrationStarted] = useState(false);
  const [dataUploadID, setDataUploadID] = useState(sessionId);
  const { startTracking, stopTracking } =
    useFormCompletionTracker("Data Migration");
  const navigate = useNavigate();
  const zeroProgressAttemptsRef = useRef(0);
  const { toast } = useToast();

  // Refs for cleanup
  const pollingIntervalRef = useRef(null);
  const migrationStartTimeRef = useRef(null);
  const pollingAttemptCountRef = useRef(0); // Use ref instead of variable to persist across renders

  // Constants for polling behavior
  const POLLING_INTERVAL_MS = 5000; // 5 seconds

  // Format timestamp for logs
  const getTimestamp = () => {
    return new Date().toLocaleTimeString("en-US", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Add log entry
  const addLog = (message) => {
    const timestamp = getTimestamp();
    setMigrationLogs((prev) => prev + `[${timestamp}] ${message}\n`);
  };

  // Start Migration Handler
  const handleStartMigration = async () => {
    if (isMigrationStarted) return;

    setIsMigrationStarted(true);
    setIsLoading(true);
    pollingAttemptCountRef.current = 0;

    try {
      setMigrationLogs("Starting migration process...\n");
      migrationStartTimeRef.current = Date.now();

      // ✅ Call new API with sessionId only
      const response = await startMigration(sessionId);

      // ✅ Extract from response.result
      if (response?.result?.taskId) {
        const taskId = response.result.taskId;
        setTaskId(taskId);
        addLog("Migration started successfully.");
        addLog(`Task ID: ${taskId}`);
        addLog(`Message: ${response.result.message}`);

        setCurrentStep("progress");
        startPolling(taskId, sessionId); // ✅ Pass sessionId instead of upload_id
        setDataUploadID(sessionId);
      } else {
        throw new Error("No task ID received from server");
      }
    } catch (error) {
      setIsMigrationStarted(false);
      addLog(`Error while starting migration: ${error.message}`);
      toast({
        variant: "destructive",
        title: "Migration Failed",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Start polling for migration status - FIXED VERSION
  const startPolling = (taskId, sessionId) => {
    // ✅ Changed parameter name
    startTracking();

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    const pollStatus = async () => {
      pollingAttemptCountRef.current += 1;
      const attemptCount = pollingAttemptCountRef.current;

      addLog(`Polling migration status (Attempt ${attemptCount})...`);

      try {
        // ✅ Use sessionId instead of uploadId
        const response = await getErrorLogs(sessionId, taskId);

        if (response?.result) {
          const result = response.result;
          setMigrationData(result);

          const completionPercentage = isNaN(result.completionPercentage)
            ? 0
            : Math.round(result.completionPercentage);

          if (completionPercentage === 0) {
            zeroProgressAttemptsRef.current += 1;
          } else {
            zeroProgressAttemptsRef.current = 0;
          }

          addLog(
            `Status: ${result.status}, Processed: ${result.processed || 0}/${
              result.total || 0
            } (${completionPercentage}%)`,
          );

          if (result.errors?.length > 0) {
            setErrorLogs(result.errors);
            addLog(
              "Errors detected during migration. See Error Log for details.",
            );
          }

          const isCompleted =
            result.completionPercentage >= 100 ||
            result.status === "completed" ||
            result.status === "finished" ||
            result.status === "success";

          const isFailed =
            result.status === "failed" ||
            result.status === "error" ||
            result.status === "permanently_failed";

          if (isCompleted) {
            addLog("✅ Migration completed successfully!");
            if (result.processingTime)
              addLog(`Processing time: ${result.processingTime}`);
            if (result.successRate)
              addLog(`Success rate: ${result.successRate}%`);
            if (result.errorRate) addLog(`Error rate: ${result.errorRate}%`);
            clearPolling();
          } else if (isFailed) {
            addLog("❌ Migration failed!");
            clearPolling();
          } else if (zeroProgressAttemptsRef.current >= 5) {
            addLog("❌ No progress after 5 attempts. Stopping migration.");
            clearPolling();
            toast({
              title: "Migration Stopped",
              description:
                "Migration did not make any progress after 5 attempts.",
              variant: "destructive",
            });
          }
        } else {
          addLog("⚠️ No valid migration status data received from server.");
        }
      } catch (error) {
        addLog(`❌ Error checking migration status: ${error.message}`);
      }
    };

    const clearPolling = () => {
      stopTracking();
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsMigrationStarted(false);
    };

    pollingIntervalRef.current = setInterval(pollStatus, POLLING_INTERVAL_MS);
    pollStatus();
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Navigation handlers
  const handlePrevious = () => {
    if (currentStep === "progress") {
      // Stop polling if going back
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setCurrentStep("ready");
      setIsMigrationStarted(false);
    } else if (currentStep === "report") {
      setCurrentStep("progress");
    } else {
      navigate(-1);
    }
  };

  const handleNext = () => {
    if (currentStep === "ready") {
      // Handle next from ready state if needed
      // This might not be used since we auto-navigate on start
    } else if (currentStep === "progress") {
      // Allow next regardless - user can manually proceed
      setCurrentStep("report");

      // Stop polling when moving to report
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  };

  const handleReturnToDashboard = () => {
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Reset all state
    setCurrentStep("ready");
    setTaskId(null);
    setMigrationData(null);
    setMigrationLogs("");
    setErrorLogs([]);
    setIsMigrationStarted(false);
    setIsLoading(false);
    pollingAttemptCountRef.current = 0;

    // ✅ Navigate to dashboard
    navigate("/onboarding/data-migration/data-cards");
  };

  const handleDownloadErrorReport = async () => {
    // if (!taskId || !uploadId) {
    //   alert("Missing task ID or upload ID");
    //   return;
    // }

    try {
      // Call API to get Blob
      const blob = await downloadErrorReport(taskId, dataUploadID);

      // Create a link and download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `migration_errors_${taskId}_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Error",
        description: `${error.message}`,
      });
    }
  };

  // Render appropriate component based on current step
  switch (currentStep) {
    case "ready":
      return (
        <StartMigration
          onStartMigration={handleStartMigration}
          onPrevious={handlePrevious}
          onNext={handleNext}
          uploadId={sessionId}
          utilityId={sessionId}
          isLoading={isLoading}
        />
      );
    case "progress":
      return (
        <MigrationProgress
          migrationData={migrationData}
          migrationLogs={migrationLogs}
          errorLogs={errorLogs}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      );
    case "report":
      return (
        <MigrationReport
          migrationData={migrationData}
          errorLogs={errorLogs}
          onReturnToDashboard={handleReturnToDashboard}
          onDownloadErrorReport={handleDownloadErrorReport}
          uploadId={sessionId}
        />
      );
    default:
      return null;
  }
};

export default MigrationExecution;
